import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MessageSquare, User, Phone, Clock, Send, CheckCircle, Circle, RefreshCw, Mail, Reply } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MessagesPage() {
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: 'all', replied: 'all' });
  const [replyModal, setReplyModal] = useState({ open: false, message: null });
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter.status !== 'all') params.status = filter.status;
      if (filter.replied !== 'all') params.replied = filter.replied === 'true';

      const [messagesRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/messages`, { params, withCredentials: true }),
        axios.get(`${API_URL}/api/messages/stats`, { withCredentials: true })
      ]);

      setMessages(messagesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, [filter]);

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API_URL}/api/messages/${messageId}`, null, {
        params: { status: 'read' },
        withCredentials: true
      });
      fetchMessages();
    } catch (error) {
      console.error('Error updating message:', error);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !replyModal.message) return;
    
    try {
      setSending(true);
      await axios.post(`${API_URL}/api/messages/${replyModal.message.message_id}/reply`, null, {
        params: { reply_text: replyText },
        withCredentials: true
      });
      setReplyModal({ open: false, message: null });
      setReplyText('');
      fetchMessages();
    } catch (error) {
      console.error('Error sending reply:', error);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getStatusBadge = (msg) => {
    if (msg.replied) {
      return <Badge className="bg-green-100 text-green-800">✓ Replied</Badge>;
    }
    if (msg.status === 'read') {
      return <Badge className="bg-blue-100 text-blue-800">Read</Badge>;
    }
    return <Badge className="bg-yellow-100 text-yellow-800 animate-pulse">New</Badge>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="messages-page">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-purple-700">{stats.total || 0}</p>
            <p className="text-sm text-purple-600">Total Messages</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-yellow-700">{stats.new || 0}</p>
            <p className="text-sm text-yellow-600">New</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-700">{stats.read || 0}</p>
            <p className="text-sm text-blue-600">Read</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-700">{stats.replied || 0}</p>
            <p className="text-sm text-green-600">Replied</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-700">{stats.pending_reply || 0}</p>
            <p className="text-sm text-red-600">Pending Reply</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="read">Read</SelectItem>
            <SelectItem value="replied">Replied</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filter.replied} onValueChange={(v) => setFilter({ ...filter, replied: v })}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Reply Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="false">Pending Reply</SelectItem>
            <SelectItem value="true">Replied</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" onClick={fetchMessages} disabled={loading} className="ml-auto">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Messages List */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-10 text-gray-500">Loading messages...</div>
        ) : messages.length === 0 ? (
          <div className="text-center py-10 text-gray-500">
            <Mail className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No messages yet</p>
          </div>
        ) : (
          messages.map((msg) => (
            <Card 
              key={msg.message_id} 
              className={`hover:shadow-md transition-shadow ${msg.status === 'new' ? 'border-l-4 border-l-yellow-400' : ''}`}
              data-testid={`message-${msg.message_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-purple-600" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900">{msg.customer_name || 'Unknown'}</span>
                      {getStatusBadge(msg)}
                      <span className="text-xs text-gray-400 capitalize">via {msg.source}</span>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {msg.phone}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(msg.created_at)}
                      </span>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-3 mb-2">
                      <p className="text-gray-800">{msg.message}</p>
                    </div>
                    
                    {msg.replied && msg.reply_text && (
                      <div className="bg-green-50 rounded-lg p-3 border-l-2 border-green-400">
                        <p className="text-xs text-green-600 mb-1 flex items-center gap-1">
                          <Reply className="w-3 h-3" />
                          Your reply ({formatDate(msg.replied_at)}):
                        </p>
                        <p className="text-gray-700 text-sm">{msg.reply_text}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-shrink-0 flex flex-col gap-2">
                    {msg.status === 'new' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => markAsRead(msg.message_id)}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Mark Read
                      </Button>
                    )}
                    {!msg.replied && (
                      <Button 
                        size="sm" 
                        className="bg-purple-600 hover:bg-purple-700"
                        onClick={() => {
                          setReplyModal({ open: true, message: msg });
                          setReplyText('');
                        }}
                      >
                        <Send className="w-4 h-4 mr-1" />
                        Reply
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Reply Modal */}
      {replyModal.open && replyModal.message && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Reply className="w-5 h-5" />
                Reply to {replyModal.message.customer_name || 'Customer'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Original message:</p>
                <p className="text-gray-800">{replyModal.message.message}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium">Your Reply</label>
                <textarea
                  className="w-full mt-1 p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows={4}
                  placeholder="Type your reply here..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  This will be sent via WhatsApp to {replyModal.message.phone}
                </p>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1" 
                  onClick={() => setReplyModal({ open: false, message: null })}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-purple-600 hover:bg-purple-700" 
                  onClick={sendReply}
                  disabled={!replyText.trim() || sending}
                >
                  {sending ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Reply
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
